import { Chip } from "@mui/material";
import type { AutocompleteInputChangeReason } from "@mui/material";
import { isEmpty } from "lodash";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  isTypeCodeableConcept,
  isTypeCoding,
} from "../../../../types/codeableConcept";
import { FreeOptionType } from "../../../../types/freeOptions";
import { createSlug } from "../../../../utils/createSlug";
import AutocompleteFreeOptions from "../../AutocompleteFreeOptions";
import { buildTypeaheadFilterOptions } from "../typeaheadFilterOptions";
import { resolveTypeaheadOption } from "../typeaheadValueUtils";
import type { ResolvedTypeaheadSectionLabels } from "../typeaheadOptionSections";

type Props = {
  disabled?: boolean;
  freeOptions?: boolean;
  onItemSelection: (val: any, reason: string) => void;
  value?: any;
  options: any[];
  optionType: string;
  loading?: boolean;
  fieldLabel: string;
  required?: boolean;
  size?: "small" | "medium";
  variant?: "standard" | "filled" | "outlined";
  getOptionValue?: (a: any) => void;
  setInputValue: (value: string) => void;
  isOptionEqualToValue?: (option: any, value: any) => boolean;
  disableCloseOnSelect?: boolean;
  getOptionLabel;
  subset?: string[];
  isRemoteSearch?: boolean;
  searchQuery?: string;
  onDropdownOpen?: () => void;
  sectionLabels?: ResolvedTypeaheadSectionLabels;
};

function SingleInput({
  disabled,
  onItemSelection,
  value,
  options,
  optionType,
  loading,
  fieldLabel,
  required,
  size,
  variant,
  getOptionValue,
  setInputValue,
  isOptionEqualToValue,
  disableCloseOnSelect,
  getOptionLabel,
  freeOptions = false,
  subset,
  isRemoteSearch = false,
  searchQuery = "",
  onDropdownOpen,
  sectionLabels,
}: Props) {
  const isEditingRef = useRef(false);
  const isTypeMedication = (item) => item && item.type === "medications";

  const findValueInOptions = (item, availableOptions, type) => {
    if (!item || !availableOptions?.length) return undefined;

    if (type === "snomed") {
      return availableOptions.find((o) =>
        isTypeCodeableConcept(item)
          ? o.snomedCID === item.coding[0].code
          : o.snomedCID === item.code,
      );
    }

    return availableOptions.find((o) =>
      isTypeCodeableConcept(item)
        ? o.code === item.coding[0].code
        : isTypeCoding(item)
          ? o.code === item.code
          : isTypeMedication(item)
            ? o.medicationCode === item.identifier
            : o.code === item.identifier,
    );
  };

  const resolveFromValue = (item, availableOptions: any[] = []) =>
    resolveTypeaheadOption(item, availableOptions, optionType, (entry, opts) =>
      findValueInOptions(entry, opts, optionType),
    );

  const getLabel = (option) => {
    if (!option) return "";
    if (getOptionLabel) {
      return getOptionLabel(option) || option.display || option.text || "";
    }
    if (optionType === "snomed") {
      return option.snomedFSN;
    }
    return option.display || "";
  };

  const [componentValue, setComponentValue] = useState<any>(() =>
    value ? resolveFromValue(value) : null,
  );
  const [inputValue, setInputValueState] = useState(() =>
    value ? getLabel(resolveFromValue(value, options)) : "",
  );

  // Sync display when the persisted form value changes (e.g. load record, parent reset).
  useEffect(() => {
    if (!value) {
      setComponentValue(null);
      setInputValueState("");
      isEditingRef.current = false;
      return;
    }

    if (isEditingRef.current) return;

    const resolved = resolveFromValue(value, options);
    setComponentValue(resolved);
    setInputValueState(getLabel(resolved));
  }, [value, optionType]);

  const handleInputChange = (
    _event: React.SyntheticEvent,
    newInput: string,
    reason: AutocompleteInputChangeReason,
  ) => {
    if (reason === "input") {
      isEditingRef.current = true;
      setComponentValue(null);
      setInputValueState(newInput);

      const trimmedInput = isEmpty(newInput.trim()) ? "" : newInput;
      setInputValue(trimmedInput);
      return;
    }

    if (reason === "clear") {
      isEditingRef.current = false;
      setInputValueState("");
      setInputValue("");
      return;
    }

    if (reason === "reset") {
      // Ignore MUI reset while the user is editing (e.g. backspacing to empty).
      if (isEditingRef.current) return;
      setInputValueState(newInput);
    }
  };

  const handleFieldBlur = () => {
    // Create-on-blur runs first (awaited). On success it calls onChange →
    // selectInputValue, which clears isEditingRef. If we are still editing,
    // blur was skipped (e.g. preventBlur) or create failed — restore/clear
    // so we don't leave orphan typed text with a null selection.
    if (!isEditingRef.current) return;

    isEditingRef.current = false;

    if (!value) {
      setComponentValue(null);
      setInputValueState("");
      setInputValue("");
      return;
    }

    const resolved = resolveFromValue(value, options);
    setComponentValue(resolved);
    setInputValueState(getLabel(resolved));
    setInputValue("");
  };

  const selectInputValue = (val, reason) => {
    if (val) {
      isEditingRef.current = false;
      onItemSelection(val, reason);
      const selected = getOptionValue ? getOptionValue(val) : val;
      setComponentValue(selected);
      setInputValueState(getLabel(val));
      setInputValue("");
    } else {
      isEditingRef.current = false;
      onItemSelection(undefined, reason);
      setComponentValue(null);
      setInputValueState("");
      setInputValue("");
    }
  };

  const filterOptions = useMemo(
    () =>
      buildTypeaheadFilterOptions({
        isRemoteSearch,
        freeOptions,
        getLabel,
        createCreatableOption: (inputValue) =>
          optionType === "snomed"
            ? {
                snomedCID: createSlug(inputValue, "other"),
                snomedFSN: `Add "${inputValue}"`,
                isCreatable: true,
              }
            : {
                code: createSlug(inputValue, "other"),
                display: `Add "${inputValue}"`,
                isCreatable: true,
              },
      }),
    [isRemoteSearch, freeOptions, optionType, getOptionLabel],
  );

  return (
    <AutocompleteFreeOptions
      type={[optionType, ...(subset || [])].join("-") as FreeOptionType}
      isRemoteSearch={isRemoteSearch}
      searchQuery={searchQuery}
      allowCreateOnBlur={freeOptions}
      disabled={disabled}
      options={options}
      getOptionLabel={getLabel}
      autoHighlight={freeOptions}
      inputValue={inputValue}
      onOpen={onDropdownOpen}
      sectionLabels={sectionLabels}
      onChange={(e, val, reason) => {
        selectInputValue(val, reason);
      }}
      value={componentValue || null}
      renderTags={(value, getTagProps) => {
        return value.map((option, index) => {
          return (
            <Chip
              label={option}
              {...getTagProps({ index })}
              deleteIcon={disabled ? <></> : null}
            />
          );
        });
      }}
      isOptionEqualToValue={isOptionEqualToValue}
      onInputChange={handleInputChange}
      textFieldProps={{
        label: fieldLabel,
        required,
        variant: variant || "outlined",
        size: size || "medium",
        InputLabelProps: { shrink: true, required },
        onBlur: handleFieldBlur,
      }}
      filterSelectedOptions
      filterOptions={filterOptions}
      id="tags-standard"
      clearOnBlur={false}
      disableCloseOnSelect={disableCloseOnSelect}
      loading={loading}
      getExistingOption={(val, op: Record<string, unknown>[]) =>
        op.find((o) => getLabel(o).toLowerCase() === val.toLowerCase())
      }
    />
  );
}

export default SingleInput;
